import { InferenceCollection } from "../types/inferences";

function escapeStringField(value: string | null | undefined): string {
  // escape commas, double-quotes and newlines
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes("\"") || str.includes("\n") || str.includes("\r")) {
    return `"${str.replace(/"/g, "''")}"`;
  }
  return str;
}

/**
 * Generates a CSV download URL from inference collection data
 */
export function generateCsvDownloadUrl(results: InferenceCollection): string {
  const excludeKeys = ["pixel_coords"];

  // Check if any feature has composite_id to determine if we should include composite columns
  const hasCompositeData = results.features.some(
    (feature) => feature.properties?.composite_id !== undefined
  );

  const inferenceHeaders = results.features.length
    ? Object.keys(results.features[0].properties?.inference).filter(
      (key) => !excludeKeys.includes(key)
    )
    : [];

  const formattedInferenceHeaders = inferenceHeaders.map((a) =>
    a
      .replace(/_/g, " ")
      .replace(/-/g, " ")
      .split(" ")
      .map((w) => w[0].toUpperCase() + w.substring(1).toLowerCase())
      .join(" ")
  );

  formattedInferenceHeaders.splice(-2, 2, ...formattedInferenceHeaders.slice(-2).map(str => `${str} (m)`));

  const csvResults = results?.features
    .sort((a, b) => {
      const dateDiff =
        new Date(a.properties.scene.date).getTime() -
        new Date(b.properties.scene.date).getTime();
      if (dateDiff !== 0) return dateDiff;
      return (
        (a.properties.composite_id ?? Infinity) -
        (b.properties.composite_id ?? Infinity)
      );
    })
    .map(({ geometry, properties }) => {
      // For Point geometry, coordinates are [lon, lat]
      // For Polygon geometry, coordinates are [[[lon, lat], ...]]
      // We'll use the centroid or first coordinate for Polygon
      const coords = geometry.type === "Point"
        ? geometry.coordinates
        : geometry.coordinates[0][0]; // First coordinate of polygon

      const baseData = [
        new Date(properties?.scene.date).toLocaleDateString("en-US", {
          timeZone: "America/Los_Angeles",
        }),
        new Date(properties?.scene.date).toLocaleTimeString("en-US", {
          timeZone: "America/Los_Angeles",
          timeZoneName: "shortOffset",
        }),
        properties?.scene.s3_prefix,
      ];

      // Add composite fields if composite data exists
      if (hasCompositeData) {
        baseData.push(
          properties?.composite_id?.toString() || "",
          properties?.composite_count?.toString() || ""
        );
      }

      return [
        ...baseData,
        properties?.inference.pixel_coords.ymin,
        properties?.inference.pixel_coords.xmin,
        properties?.inference.pixel_coords.ymax,
        properties?.inference.pixel_coords.xmax,
        coords[0].toFixed(4),
        coords[1].toFixed(4),
        properties?.is_valid,
        inferenceHeaders.map((key) => {
          const value = properties?.inference[key];
          return typeof value === "number" ? value.toFixed(2) : value;
        }),
        escapeStringField(properties?.notes),
        properties?.selected_class,
        properties?.flagged,
      ];
    })
    .map((line) => `${line.join(",")}\n`);

  // Build header row with composite fields if composite data exists
  const baseHeaders = "Scene Date,Scene Time,Scene ID";
  const compositeHeaders = hasCompositeData ? ",Composite ID,Composite Count" : "";
  const remainingHeaders = `ymin,xmin,ymax,xmax,Lon,Lat,Valid,${formattedInferenceHeaders},Notes,Selected Class,Flagged`;

  const content = `${baseHeaders}${compositeHeaders},${remainingHeaders}\n${csvResults?.join("")}`;
  const blob = new Blob([content], { type: "text/csv" });
  return window.URL.createObjectURL(blob);
}
